/**
 * A regra da casa entre o método de pagamento e a caixa:
 *
 *   - Dinheiro      → só a caixa "Clínica" (é dinheiro físico, fica na gaveta).
 *   - Tudo o resto  → nunca a caixa "Clínica" (o dinheiro passou por um banco).
 *
 * Estava só no ecrã de recebimento de parcelas, e por isso as despesas
 * continuavam a nascer com a caixa errada — são a origem de todos os casos
 * recentes. Passou para aqui para ser a mesma regra em todo o lado.
 *
 * As listas de caixas destes ecrãs são `{ id, value }`, em que `value` é o
 * nome. A comparação é tolerante a acentos, maiúsculas e espaços a mais:
 * "Clínica", "clinica" e " CLINICA " são a mesma caixa.
 */

export const METODO_DINHEIRO = "Dinheiro";

const normalizar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const ehCaixaClinica = (nome) => normalizar(nome) === "clinica";

export const ehDinheiro = (metodo) => normalizar(metodo) === "dinheiro";

/**
 * As caixas que o método permite.
 *
 * Uma clínica sem caixa "Clínica" não tem regra que se aplique — devolvem-se
 * todas, para não ficar um seletor vazio que impede o trabalho.
 */
export const caixasPermitidas = (caixas = [], metodo) => {
  const daClinica = caixas.filter((c) => ehCaixaClinica(c.value));
  if (daClinica.length === 0) return caixas;

  return ehDinheiro(metodo)
    ? daClinica
    : caixas.filter((c) => !ehCaixaClinica(c.value));
};

/**
 * As opções a mostrar no seletor.
 *
 * É `caixasPermitidas` mais a caixa que o registo já tem, quando a regra já
 * não a permitiria — os registos antigos são anteriores à regra, e retirá-la
 * da lista deixava o seletor a mostrar uma caixa que não é a do registo.
 */
export const opcoesDeCaixa = (caixas = [], metodo, caixaAtualId) => {
  const permitidas = caixasPermitidas(caixas, metodo);
  if (permitidas.some((c) => c.id === caixaAtualId)) return permitidas;

  const atual = caixas.find((c) => c.id === caixaAtualId);
  return atual ? [atual, ...permitidas] : permitidas;
};

/**
 * Mudou o método: devolve a caixa que deve ficar escolhida.
 * Em dinheiro é a "Clínica"; nos outros, a atual se ainda for permitida, senão
 * a primeira permitida.
 */
export const caixaParaMetodo = (caixas = [], metodo, caixaAtualId) => {
  const permitidas = caixasPermitidas(caixas, metodo);
  if (permitidas.length === 0) return caixaAtualId;

  const atualContinua = permitidas.some((c) => c.id === caixaAtualId);
  return atualContinua ? caixaAtualId : permitidas[0].id;
};

/**
 * Mudou a caixa: devolve o método que deve ficar escolhido.
 * A caixa "Clínica" força Dinheiro; sair dela, estando em Dinheiro, obriga a
 * escolher outro método — devolve-se o primeiro que não seja Dinheiro.
 */
export const metodoParaCaixa = (caixas = [], caixaId, metodoAtual, metodos = []) => {
  const escolhida = caixas.find((c) => c.id === caixaId);
  if (!escolhida) return metodoAtual;

  if (ehCaixaClinica(escolhida.value)) return METODO_DINHEIRO;

  if (ehDinheiro(metodoAtual)) {
    return metodos.find((m) => !ehDinheiro(m)) || metodoAtual;
  }
  return metodoAtual;
};
